import classNames from 'classnames'
import React, { useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '@context/SnackbarContext'

import { ActionIcon } from '@ui/components/ActionIcon'
import { Input } from '@ui/components/Input/Input'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { CaretIcon } from '@ui/icons/Caret.icon'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { copyToClipboard } from '@utils/navigator.utils'

import { getLabel, getPlaceholder } from '../formField/helpers'

import styles from './SelectField.module.scss'

interface Option {
  value?: string | number
  label: string | number
}

export interface SelectFieldProps {
  label?: string
  placeholder?: string
  name?: string
  hint?: string
  required?: boolean
  disabled?: boolean
  tooltip?: string
  error?: string
  allowCopy?: boolean
  onChange?(value: React.ChangeEvent<HTMLSelectElement>): void
  trackEvent?(): void
  onClick?(toggleDropdown: () => void): void
  value?: string | number
  renderTooltip?(): React.ReactNode
  options?: Option[]
  radio?: boolean
  renderValue?: React.ReactNode
  allowEmpty?: boolean
  fullWidth?: boolean
  readOnly?: boolean
  showLabel?: boolean
  wrapText?: boolean
}

export function SelectField(props: SelectFieldProps): JSX.Element {
  const {
    name = '',
    hint,
    tooltip,
    renderTooltip,
    allowCopy,
    onClick,
    error,
    value,
    radio,
    options = [],
    disabled = !options || options.length < 2,
    renderValue,
    allowEmpty,
    trackEvent,
    fullWidth = false,
    showLabel,
    ...inputProps
  } = props
  const { addSnackbar } = useSnackbar()

  useEffect(() => {
    if (options.length !== 1) {
      return
    }
    inputProps.onChange?.({
      target: {
        name,
        value: options[0].value,
      },
    } as React.ChangeEvent<HTMLSelectElement>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length])

  const { t } = useTranslation()
  const fieldRef = useRef<HTMLDivElement>(null)

  const baseLabel = getLabel(props)
  const rawLabel = baseLabel ?? t('Select')
  const rawPlaceholder = getPlaceholder(props) ?? t('Select')
  const hasValue = value !== undefined && value !== null && value !== ''

  const selectOptions = useMemo(
    () => [
      ...(allowEmpty ? [{ value: '', label: rawPlaceholder || t('Select') }] : []),
      ...options,
    ],
    [allowEmpty, options, rawPlaceholder, t]
  )

  const tooltipNode = renderTooltip ? (
    renderTooltip()
  ) : tooltip ? (
    <Tooltip content={tooltip}>
      <InfoIcon color='text-info-med-em' />
    </Tooltip>
  ) : null

  const [isOpen, setIsOpen] = useState(false)

  const toggleDropdown = (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e) {
      e.stopPropagation()
    }
    if (disabled) {
      return
    }
    if (trackEvent) {
      trackEvent()
    }
    if (onClick) {
      onClick(() => setIsOpen(!isOpen))
    } else {
      setIsOpen(!isOpen)
    }
  }

  const handleOptionSelect = (option: Option) => {
    inputProps.onChange?.({
      target: {
        name,
        value: option.value,
      },
    } as React.ChangeEvent<HTMLSelectElement>)
    setIsOpen(false)
  }

  const selectedLabel = useMemo(() => {
    if (renderValue) {
      return undefined
    }
    const selectedOption = selectOptions.find((option) => option.value === value)
    return selectedOption ? String(selectedOption.label) : ''
  }, [value, selectOptions, renderValue])

  const rightIcon = isOpen ? CaretIcon.Up : CaretIcon.Down

  const handleWrapperClick = () => {
    toggleDropdown()
  }

  const inputValue = renderValue ? String(renderValue) : selectedLabel || ''
  const renderLabel = showLabel === false || hasValue ? '' : rawLabel

  const renderDropdownOptions = () => {
    return selectOptions.map((option) => {
      const isSelected = option.value === value
      return (
        <ListItem
          key={option.value}
          onClick={() => handleOptionSelect(option)}
          active={isSelected}
          endSlot={radio ? { radio: true } : undefined}
        >
          {String(option.label)}
        </ListItem>
      )
    })
  }

  return (
    <div
      className={classNames(styles.field, {
        [styles.fullWidth]: fullWidth,
      })}
      ref={fieldRef}
    >
      {tooltipNode && (
        <div style={{ marginBottom: '0.25rem' }}>
          <label
            className={classNames('label', styles.label, {
              [styles.hasTextDanger]: !!error,
            })}
          >
            {showLabel && rawLabel}
            {tooltipNode}
          </label>
        </div>
      )}
      <Popover
        direction='top'
        isOpen={isOpen}
        label={rawLabel}
        setIsOpen={setIsOpen}
        disabled={disabled}
        content={
          <PopoverCard>
            <PopoverList variant='compact'>{renderDropdownOptions()}</PopoverList>
          </PopoverCard>
        }
      >
        <Input
          value={inputValue}
          onWrapperClick={handleWrapperClick}
          label={renderLabel}
          placeholder={rawPlaceholder}
          rightIcon={rightIcon}
          hint={hint}
          error={!!error}
          showLabel={showLabel}
          readOnly
          disabled={disabled}
          rightContent={
            allowCopy && value ? (
              <ActionIcon
                icon={{ icon: CopyIcon, color: 'text-low-em' }}
                onClick={(e) => {
                  e.stopPropagation()
                  copyToClipboard(value)
                  addSnackbar.success({ message: t('Copied') })
                }}
                size='x16'
              />
            ) : undefined
          }
          name={name}
          size='xl'
        />
      </Popover>
    </div>
  )
}
